/**
 * Shared search result row presentation
 *
 * Renders an optional entity-type badge (hidden for the default "Player"
 * type), player name, position/club subtitle, a metric badge, and an
 * action slot. Visual structure matches both AddPlayerToList and
 * GlobalSearch result rows.
 * @module components/shared/search-modal/PlayerSearchResultRow/PlayerSearchResultRowView
 */

import React, { memo, type ReactNode } from "react";

import InfoBadge from "@/components/organisms/shared/search-modal/InfoBadge/InfoBadgeView";
import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

/**
 * Entity type a search result row represents.
 * v1 only renders "Player" rows; "Report" / "Note" are scaffolded for
 * future entity types and surface a leading badge identifying the type.
 */
export type SearchResultEntityType = "Player" | "Report" | "Note";

/** Background + text classes for the leading entity-type badge */
const ENTITY_BADGE_CLASSES: Record<
  Exclude<SearchResultEntityType, "Player">,
  string
> = {
  Report: "bg-needs-semantic-warning-100 text-needs-semantic-warning-700",
  Note: "bg-needs-semantic-info-100 text-needs-semantic-info-700",
};

/**
 * Props for PlayerSearchResultRowView
 */
interface PlayerSearchResultRowViewProps {
  /** Full player name */
  readonly playerName: string;
  /** Position abbreviation (e.g. "ST", "CM") */
  readonly position: string;
  /** Club name */
  readonly clubName: string;
  /** Label for the metric badge (e.g. "GPR", "Style Fit") */
  readonly metricLabel: string;
  /** Metric value string or null to hide badge */
  readonly metricValue: string | null;
  /** Action element rendered on the right side */
  readonly action: ReactNode;
  /** Optional press handler for the row */
  readonly onPress?: () => void;
  /**
   * Entity type the row represents. Defaults to "Player"; leading
   * entity-type badge is rendered only for non-Player rows so existing
   * Player call sites are visually unaffected.
   */
  readonly entityType?: SearchResultEntityType;
}

/**
 * Renders a player search result row with metric badge and action slot
 * @param root0 - Component props
 * @param root0.playerName - Player display name
 * @param root0.position - Position abbreviation
 * @param root0.clubName - Club name
 * @param root0.metricLabel - Metric badge label
 * @param root0.metricValue - Metric badge value (null hides badge)
 * @param root0.action - Action slot content
 * @param root0.onPress - Optional row press handler
 * @param root0.entityType - Entity type the row represents (default "Player")
 */
const PlayerSearchResultRowView = ({
  playerName,
  position,
  clubName,
  metricLabel,
  metricValue,
  action,
  onPress,
  entityType = "Player",
}: PlayerSearchResultRowViewProps) => (
  <Pressable
    onPress={onPress}
    /*
     * A row with no handler must not LOOK pressable (SE-7242). Quick capture
     * renders players already on the report as inert "In squad" rows, and a
     * cursor plus a hover highlight over a row that does nothing is how a
     * scout concludes the screen is broken. Derived from `onPress` rather
     * than a new flag so every existing caller — all of which pass one —
     * keeps its current appearance.
     */
    className={`border-b border-outline-default native:border-b-0 ${
      onPress === undefined
        ? ""
        : "web:cursor-pointer web:hover:bg-surface-raised"
    }`}
  >
    <Stack direction="horizontal" className="items-center gap-4 px-6 py-3">
      {entityType !== "Player" ? (
        <Box
          className={`rounded-none px-2 py-micro ${ENTITY_BADGE_CLASSES[entityType]}`}
        >
          <Text variant="unstyled" className="font-medium text-micro uppercase">
            {entityType}
          </Text>
        </Box>
      ) : null}

      <Stack className="flex-1">
        <Text variant="title-sm" className="h-6 text-content-primary">
          {playerName}
        </Text>
        <Text
          variant="micro"
          className="h-6 text-content-muted native:text-content-secondary"
          {...({ numberOfLines: 1 } as Record<string, number>)}
        >
          {position} • {clubName}
          {metricValue !== null ? (
            <Text
              variant="micro"
              className="web:hidden text-content-muted native:text-content-secondary"
            >
              {` • ${metricLabel} ${metricValue}`}
            </Text>
          ) : null}
        </Text>
      </Stack>

      {metricValue !== null ? (
        <Stack direction="horizontal" className="native:hidden">
          <InfoBadge label={`${metricLabel}: ${metricValue}`} />
        </Stack>
      ) : null}

      {/*
       * Shrink-protect the action slot so the flex-1 name/subtitle Stack
       * above cannot compress it on narrow screens. Without this the action
       * button is squeezed until its label wraps, and a fixed-height button
       * (e.g. size="sm" h-9) clips the second line — the cause of the
       * "Add To Academy" → "Add To" truncation on small Android screens
       * (SE-4837). The name Stack already truncates via numberOfLines.
       */}
      <Box className="shrink-0">{action}</Box>
    </Stack>
  </Pressable>
);

PlayerSearchResultRowView.displayName = "PlayerSearchResultRowView";

export default memo(PlayerSearchResultRowView);
